/**
 * 应收报表历史归档
 */
import type {
  ReceivableCustomerItem,
  ReceivableCustomerQueryParams,
} from '../receivableCustomer';

export type ReceivableHistoryItem = ReceivableCustomerItem & {
  historyCode?: string;
  historyName?: string;
};

export type ReceivableHistoryQueryParams = ReceivableCustomerQueryParams & {
  /** 归档版本 code，可空 */
  historyCode?: string;
};

interface ReceivableHistoryRawItem {
  id?: string;
  historyCode?: string;
  historyName?: string;
  masterId?: string;
  dissaleChannelCode?: string;
  sapCustomerCode?: string;
  isProfessional?: number | string | boolean;
  salesOfficeRegionCode?: string;
  salesOfficeRegionName?: string;
  saleOfficeCode?: string;
  saleOfficeName?: string;
  /** 公司编码 */
  corpCode?: string;
  saleChannelName?: string;
  dissaleChannelName?: string;
  controlUserName?: string;
  controlUserCode?: string;
  custCode?: string;
  custName?: string;
  paymentConditionName?: string;
  remainingReceivableAmt?: number | string;
  xyqxzCny?: number | string;
  yqTotalCny?: number | string;
  yqMonth01Cny?: number | string;
  yqMonth02Cny?: number | string;
  yqMonth03Cny?: number | string;
  yqMonth04Cny?: number | string;
  yqMonth05Cny?: number | string;
  yqMonth06Cny?: number | string;
  yqMonth07Cny?: number | string;
  yqMonth08Cny?: number | string;
  yqMonth09Cny?: number | string;
  yqMonth10Cny?: number | string;
  yqMonth11Cny?: number | string;
  yqMonth12Cny?: number | string;
  yqMonth13Cny?: number | string;
  riskLevel?: string;
  riskReason?: string;
  riskDesc?: string;
  riskManualModified?: number | string | boolean;
  isProsecute?: number | string | boolean;
  financeCode?: string;
  financeName?: string;
  salesmanUserCode?: string;
  salesmanUserName?: string;
  managerCode?: string;
  managerName?: string;
  lDate?: string;
  dwsUpdateTime?: string;
}

interface ReceivableHistoryQueryResponse {
  data?: {
    ariesPlatformReceivableHistoryQuery?: {
      queryPage?: {
        content?: ReceivableHistoryRawItem[];
        totalElements?: number | string;
        totalPages?: number | string;
        size?: number | string;
        currentPage?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

/** 归档版本下拉项 */
export interface HistoryArchiveVersionItem {
  id: string;
  code: string;
  name: string;
  lDate?: string;
  createDate?: string;
}

interface HistoryArchiveVersionQueryResponse {
  data?: {
    ariesReceivableOverdueRiskHistoryQuery?: {
      queryPage?: {
        content?: HistoryArchiveVersionItem[];
        totalElements?: number | string;
        totalPages?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

export const RECEIVABLE_HISTORY_EXPORT_MODEL =
  'aries.platform.AriesPlatformReceivableHistory';

/** 应收报表历史归档导出接口 variables.path（固定） */
export const RECEIVABLE_HISTORY_EXPORT_PATH =
  '/aries_core_platform/uiMenua1b2c3d4e5f64789a0b1c2d3e4f5a6b7/ACTION#aries.platform.AriesPlatformReceivableHistory#internalGotoListExportDialog';

function emptyToBlank(value?: string) {
  return (value ?? '').trim();
}

function encodeBase64(value: string) {
  return window.btoa(unescape(encodeURIComponent(value)));
}

function escapeRsqlLikeValue(value: string) {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

function formatChannel(code?: string, name?: string) {
  if (name) return name;
  if (code === '10') return '10渠道';
  if (code === '20') return '20渠道';
  return code || '';
}

function isRiskManualModified(value?: number | string | boolean) {
  return value === 1 || value === '1' || value === true;
}

function formatYesNo(value?: number | string | boolean) {
  if (value === 1 || value === '1' || value === true) return '是';
  if (value === 0 || value === '0' || value === false) return '否';
  return '-';
}

function buildHistoryQueryData(params: ReceivableHistoryQueryParams) {
  const data: Record<string, string | boolean> = {
    historyCode: emptyToBlank(params.historyCode),
    salesOfficeRegionCode: emptyToBlank(params.salesOfficeRegionCode),
    saleOfficeCode: emptyToBlank(params.saleOfficeCode),
    sapCustomerCode: emptyToBlank(params.sapCustomerCode),
    financeCode: emptyToBlank(params.financeCode),
    salesmanUserCode: emptyToBlank(params.salesmanUserCode),
    dissaleChannelCode: emptyToBlank(params.dissaleChannelCode),
    feedbackStatus: emptyToBlank(params.feedbackStatus),
    riskLevel: emptyToBlank(params.riskLevel),
    controlUserCode: emptyToBlank(params.controlUserCode),
  };
  if (typeof params.hasControlUser === 'boolean') {
    data.hasControlUser = params.hasControlUser;
  }
  if (typeof params.isProsecute === 'boolean') {
    data.isProsecute = params.isProsecute;
  }
  if (typeof params.isOverdue === 'boolean') {
    data.isOverdue = params.isOverdue;
  }
  return data;
}

function mapReceivableHistoryItem(raw: ReceivableHistoryRawItem): ReceivableHistoryItem {
  return {
    id: String(raw.id ?? ''),
    masterId: raw.masterId != null && raw.masterId !== '' ? String(raw.masterId) : undefined,
    historyCode: raw.historyCode,
    historyName: raw.historyName,
    isProfessional: formatYesNo(raw.isProfessional),
    financeArea: raw.salesOfficeRegionName,
    corpCode: raw.corpCode,
    companyName: raw.saleOfficeName,
    saleChannelName: raw.saleChannelName,
    channel: formatChannel(raw.dissaleChannelCode, raw.dissaleChannelName),
    dissaleChannelCode: raw.dissaleChannelCode,
    controller: raw.controlUserCode,
    customerCode: raw.sapCustomerCode || raw.custCode,
    customerName: raw.custName,
    paymentTerm: raw.paymentConditionName,
    receivableBalance: raw.remainingReceivableAmt,
    creditWithin: raw.xyqxzCny,
    overdueTotal: raw.yqTotalCny,
    overdue1: raw.yqMonth01Cny,
    overdue2: raw.yqMonth02Cny,
    overdue3: raw.yqMonth03Cny,
    overdue4: raw.yqMonth04Cny,
    overdue5: raw.yqMonth05Cny,
    overdue6: raw.yqMonth06Cny,
    overdue7: raw.yqMonth07Cny,
    overdue8: raw.yqMonth08Cny,
    overdue9: raw.yqMonth09Cny,
    overdue10: raw.yqMonth10Cny,
    overdue11: raw.yqMonth11Cny,
    overdue12: raw.yqMonth12Cny,
    overdueOverYear: raw.yqMonth13Cny,
    riskLevel: raw.riskLevel,
    riskReason: raw.riskReason,
    overdueReason: raw.riskDesc,
    isProsecute: formatYesNo(raw.isProsecute),
    riskManualModified: isRiskManualModified(raw.riskManualModified),
    financeOwner: raw.financeName,
    financeOwnerNo: raw.financeCode,
    salesman: raw.salesmanUserName,
    salesmanNo: raw.salesmanUserCode,
    companyManager: raw.managerName,
    companyManagerNo: raw.managerCode,
    syncAt: raw.lDate,
  };
}

function buildReceivableHistoryGraphql(
  currentPage: number,
  size: number,
  queryData: string,
) {
  return `{
  ariesPlatformReceivableHistoryQuery {
    queryPage(
      page: {currentPage: ${currentPage}, size: ${size}}
      queryWrapper: {queryData: ${JSON.stringify(queryData)}}
    ) {
      content {
        id
        historyCode
        historyName
        masterId
        dissaleChannelCode
        sapCustomerCode
        isProfessional
        salesOfficeRegionCode
        salesOfficeRegionName
        saleOfficeCode
        saleOfficeName
        corpCode
        saleChannelName
        dissaleChannelName
        controlUserName
        controlUserCode
        custCode
        custName
        paymentConditionName
        remainingReceivableAmt
        xyqxzCny
        yqTotalCny
        yqMonth01Cny
        yqMonth02Cny
        yqMonth03Cny
        yqMonth04Cny
        yqMonth05Cny
        yqMonth06Cny
        yqMonth07Cny
        yqMonth08Cny
        yqMonth09Cny
        yqMonth10Cny
        yqMonth11Cny
        yqMonth12Cny
        yqMonth13Cny
        riskLevel
        riskReason
        riskDesc
        riskManualModified
        isProsecute
        financeCode
        financeName
        salesmanUserCode
        salesmanUserName
        managerCode
        managerName
        lDate
        dwsUpdateTime
      }
      totalElements
      totalPages
      size
      currentPage
    }
  }
}`;
}

/**
 * 应收报表历史归档分页查询
 */
export async function queryReceivableHistoryPage(
  params: ReceivableHistoryQueryParams,
): Promise<{ content: ReceivableHistoryItem[]; totalElements: number }> {
  const queryData = JSON.stringify(buildHistoryQueryData(params));
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildReceivableHistoryGraphql(params.currentPage, params.size, queryData),
    }),
  });

  if (!response.ok) {
    throw new Error(`历史归档接口请求失败: ${response.status}`);
  }

  const result: ReceivableHistoryQueryResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '历史归档 GraphQL 请求失败');
  }

  const pageData = result.data?.ariesPlatformReceivableHistoryQuery?.queryPage;
  return {
    content: (pageData?.content ?? []).map(mapReceivableHistoryItem),
    totalElements: Number(pageData?.totalElements ?? 0),
  };
}

/** 统计接口原始字段（金额合计） */
interface ReceivableHistoryStatisticalRaw {
  remainingReceivableAmt?: number | string;
  xyqxzCny?: number | string;
  yqTotalCny?: number | string;
  yqMonth01Cny?: number | string;
  yqMonth02Cny?: number | string;
  yqMonth03Cny?: number | string;
  yqMonth04Cny?: number | string;
  yqMonth05Cny?: number | string;
  yqMonth06Cny?: number | string;
  yqMonth07Cny?: number | string;
  yqMonth08Cny?: number | string;
  yqMonth09Cny?: number | string;
  yqMonth10Cny?: number | string;
  yqMonth11Cny?: number | string;
  yqMonth12Cny?: number | string;
  yqMonth13Cny?: number | string;
}

interface ReceivableHistoryStatisticalResponse {
  data?: {
    ariesPlatformReceivableHistoryQuery?: {
      queryStatisticalData?: ReceivableHistoryStatisticalRaw | null;
    };
  };
  errors?: Array<{ message?: string }>;
}

/** 列表筛选条件（不含分页） */
export type ReceivableHistoryFilterParams = Omit<
  ReceivableHistoryQueryParams,
  'currentPage' | 'size'
>;

function buildHistoryStatisticalDataGraphql(params: ReceivableHistoryFilterParams) {
  const data = buildHistoryQueryData({
    ...params,
    currentPage: 1,
    size: 1,
  });
  const lines = Object.entries(data).map(([key, value]) => {
    if (typeof value === 'boolean') {
      return `${key}: ${value}`;
    }
    return `${key}: "${escapeGraphqlString(String(value))}"`;
  });

  return `{
  ariesPlatformReceivableHistoryQuery {
    queryStatisticalData(
      data: {
        ${lines.join('\n        ')}
      }
    ) {
      remainingReceivableAmt
      xyqxzCny
      yqTotalCny
      yqMonth01Cny
      yqMonth02Cny
      yqMonth03Cny
      yqMonth04Cny
      yqMonth05Cny
      yqMonth06Cny
      yqMonth07Cny
      yqMonth08Cny
      yqMonth09Cny
      yqMonth10Cny
      yqMonth11Cny
      yqMonth12Cny
      yqMonth13Cny
    }
  }
}`;
}

function mapHistoryStatisticalToSummaryRow(
  raw: ReceivableHistoryStatisticalRaw,
): ReceivableHistoryItem {
  return {
    id: '__receivable_history_summary__',
    isSummaryRow: true,
    receivableBalance: raw.remainingReceivableAmt,
    creditWithin: raw.xyqxzCny,
    overdueTotal: raw.yqTotalCny,
    overdue1: raw.yqMonth01Cny,
    overdue2: raw.yqMonth02Cny,
    overdue3: raw.yqMonth03Cny,
    overdue4: raw.yqMonth04Cny,
    overdue5: raw.yqMonth05Cny,
    overdue6: raw.yqMonth06Cny,
    overdue7: raw.yqMonth07Cny,
    overdue8: raw.yqMonth08Cny,
    overdue9: raw.yqMonth09Cny,
    overdue10: raw.yqMonth10Cny,
    overdue11: raw.yqMonth11Cny,
    overdue12: raw.yqMonth12Cny,
    overdueOverYear: raw.yqMonth13Cny,
  };
}

/**
 * 历史归档统计合计（与列表相同筛选条件）
 */
export async function queryReceivableHistoryStatisticalData(
  params: ReceivableHistoryFilterParams,
): Promise<ReceivableHistoryItem | null> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildHistoryStatisticalDataGraphql(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`历史归档统计接口请求失败: ${response.status}`);
  }

  const result: ReceivableHistoryStatisticalResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '历史归档统计 GraphQL 请求失败');
  }

  const raw = result.data?.ariesPlatformReceivableHistoryQuery?.queryStatisticalData;
  if (!raw) return null;
  return mapHistoryStatisticalToSummaryRow(raw);
}

function buildHistoryArchiveRsql(keyword?: string) {
  const kw = (keyword ?? '').trim();
  if (!kw) {
    // 与分公司空关键字一致：不传过滤条件
    return '';
  }
  const escaped = escapeRsqlLikeValue(kw);
  return `base64:${encodeBase64(`(code=like='${escaped}') or (name=like='${escaped}')`)}`;
}

function buildHistoryArchiveGraphql(currentPage: number, size: number, rsql: string) {
  const rsqlPart = rsql ? JSON.stringify(rsql) : JSON.stringify('1==1');
  return `{
  ariesReceivableOverdueRiskHistoryQuery {
    queryPage(
      page: {currentPage: ${currentPage}, size: ${size}}
      queryWrapper: {rsql: ${rsqlPart}}
    ) {
      content {
        id
        code
        name
        lDate
        createDate
      }
      totalElements
      totalPages
    }
  }
}`;
}

export interface QueryHistoryArchiveVersionParams {
  currentPage: number;
  size: number;
  keyword?: string;
}

/**
 * 分页查询归档版本（下拉搜索）
 */
export async function queryHistoryArchiveVersionPage(
  params: QueryHistoryArchiveVersionParams,
): Promise<{
  content: HistoryArchiveVersionItem[];
  totalPages: number;
  totalElements: number;
}> {
  const rsql = buildHistoryArchiveRsql(params.keyword);
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildHistoryArchiveGraphql(params.currentPage, params.size, rsql),
    }),
  });

  if (!response.ok) {
    throw new Error(`查询归档版本失败: ${response.status}`);
  }

  const result: HistoryArchiveVersionQueryResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询归档版本失败');
  }

  const pageData = result.data?.ariesReceivableOverdueRiskHistoryQuery?.queryPage;
  return {
    content: (pageData?.content ?? []).map((item) => ({
      id: String(item.id ?? ''),
      code: String(item.code ?? ''),
      name: String(item.name ?? ''),
      lDate: item.lDate,
      createDate: item.createDate,
    })),
    totalPages: Number(pageData?.totalPages ?? 0),
    totalElements: Number(pageData?.totalElements ?? 0),
  };
}

function escapeGraphqlString(value: string) {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

/** 历史归档风险修改日志 */
export interface HistoryRiskRecordLogItem {
  originRiskLevel?: string;
  riskLevel?: string;
  riskReason?: string;
  riskDesc?: string;
  manualModified?: number | string | boolean;
  isProsecute?: number | string | boolean;
  createName?: string;
  createWorkCode?: string;
  createDate?: string;
}

export interface QueryHistoryRiskRecordLogParams {
  historyCode: string;
  dissaleChannelCode: string;
  sapCustomerCode: string;
  /** 公司编码 */
  corpCode: string;
}

interface QueryHistoryRiskRecordLogResponse {
  data?: {
    ariesPlatformReceivableHistoryQuery?: {
      queryFollowUpRiskRecordLog?: HistoryRiskRecordLogItem[];
    };
  };
  errors?: Array<{ message?: string }>;
}

function buildHistoryRiskRecordLogGraphql(params: QueryHistoryRiskRecordLogParams) {
  return `{
  ariesPlatformReceivableHistoryQuery {
    queryFollowUpRiskRecordLog(
      data: {
        historyCode: "${escapeGraphqlString(params.historyCode)}"
        dissaleChannelCode: "${escapeGraphqlString(params.dissaleChannelCode)}"
        sapCustomerCode: "${escapeGraphqlString(params.sapCustomerCode)}"
        corpCode: "${escapeGraphqlString(params.corpCode)}"
      }
    ) {
      originRiskLevel
      riskLevel
      riskReason
      riskDesc
      manualModified
      isProsecute
      createName
      createWorkCode
      createDate
    }
  }
}`;
}

/**
 * 查询历史归档风险修改日志
 */
export async function queryHistoryRiskRecordLog(
  params: QueryHistoryRiskRecordLogParams,
): Promise<HistoryRiskRecordLogItem[]> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildHistoryRiskRecordLogGraphql(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`查询修改日志请求失败: ${response.status}`);
  }

  const result: QueryHistoryRiskRecordLogResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询修改日志失败');
  }

  return result.data?.ariesPlatformReceivableHistoryQuery?.queryFollowUpRiskRecordLog ?? [];
}
